import { Paper, alpha, Box, BottomNavigation, BottomNavigationAction, Container, IconButton, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@paneon/theme';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// src/Glassmorphism.tsx
function GlassCard({
  children,
  blur = 10,
  opacity = 0.7,
  borderRadius = 16,
  elevation = 0,
  sx,
  onClick
}) {
  const { isDarkMode, currentTheme } = useTheme();
  return /* @__PURE__ */ jsx(
    Paper,
    {
      elevation,
      onClick,
      sx: {
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        backgroundColor: isDarkMode ? alpha("#1E1E1E", opacity) : alpha("#FFFFFF", opacity),
        border: `1px solid ${isDarkMode ? alpha("#FFFFFF", 0.1) : alpha("#000000", 0.1)}`,
        borderRadius: `${borderRadius}px`,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.3s ease",
        "&:hover": onClick ? {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 32px ${alpha(currentTheme.colors.primary, 0.2)}`
        } : {},
        ...sx
      },
      children
    }
  );
}
function GlassContainer({
  children,
  fullHeight = false,
  padding = 2,
  sx
}) {
  const { isDarkMode } = useTheme();
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        backgroundColor: isDarkMode ? alpha("#121212", 0.8) : alpha("#FAFAFA", 0.8),
        minHeight: fullHeight ? "100vh" : "auto",
        padding,
        ...sx
      },
      children
    }
  );
}
function GlowEffect({
  color,
  size = 300,
  intensity = 0.3,
  position = "top-right"
}) {
  const { currentTheme } = useTheme();
  const glowColor = color || currentTheme.glowColor;
  const positionStyles = {
    "top-left": { top: -size / 2, left: -size / 2 },
    "top-right": { top: -size / 2, right: -size / 2 },
    "bottom-left": { bottom: -size / 2, left: -size / 2 },
    "bottom-right": { bottom: -size / 2, right: -size / 2 },
    center: { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
  };
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        position: "absolute",
        width: size,
        height: size,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${alpha(glowColor, intensity)} 0%, transparent 70%)`,
        pointerEvents: "none",
        zIndex: 0,
        ...positionStyles[position]
      }
    }
  );
}
function BottomNav({ items, sx }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, currentTheme } = useTheme();
  const currentIndex = items.findIndex(
    (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  );
  const handleChange = (_event, newValue) => {
    const item = items[newValue];
    if (item) {
      navigate(item.path);
    }
  };
  return /* @__PURE__ */ jsx(
    Paper,
    {
      elevation: 0,
      sx: {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        backgroundColor: isDarkMode ? alpha("#1E1E1E", 0.9) : alpha("#FFFFFF", 0.9),
        borderTop: `1px solid ${isDarkMode ? alpha("#FFFFFF", 0.1) : alpha("#000000", 0.1)}`,
        // Safe area padding for iOS
        paddingBottom: "env(safe-area-inset-bottom)",
        ...sx
      },
      children: /* @__PURE__ */ jsx(
        BottomNavigation,
        {
          value: currentIndex >= 0 ? currentIndex : 0,
          onChange: handleChange,
          sx: {
            backgroundColor: "transparent",
            height: 64,
            "& .MuiBottomNavigationAction-root": {
              color: isDarkMode ? alpha("#FFFFFF", 0.5) : alpha("#000000", 0.5),
              minWidth: "auto",
              padding: "6px 12px",
              "&.Mui-selected": {
                color: currentTheme.colors.primary
              }
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.7rem",
              marginTop: "4px",
              "&.Mui-selected": {
                fontSize: "0.7rem"
              }
            }
          },
          children: items.map((item, index) => /* @__PURE__ */ jsx(
            BottomNavigationAction,
            {
              label: item.label,
              icon: currentIndex === index && item.activeIcon ? item.activeIcon : item.icon
            },
            item.path
          ))
        }
      )
    }
  );
}
function PageLayout({
  children,
  title,
  subtitle,
  showBack = false,
  backPath,
  rightAction,
  headerContent,
  noPadding = false,
  maxWidth = "lg",
  sx
}) {
  const navigate = useNavigate();
  const { isDarkMode, currentTheme } = useTheme();
  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        minHeight: "100vh",
        backgroundColor: isDarkMode ? "#121212" : currentTheme.colors.background,
        paddingBottom: "calc(64px + env(safe-area-inset-bottom))",
        // Bottom nav space
        ...sx
      },
      children: [
        (title || showBack || rightAction || headerContent) && /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              position: "sticky",
              top: 0,
              zIndex: 1e3,
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              backgroundColor: isDarkMode ? alpha("#121212", 0.9) : alpha("#FFFFFF", 0.9),
              borderBottom: `1px solid ${isDarkMode ? alpha("#FFFFFF", 0.1) : alpha("#000000", 0.1)}`,
              // Safe area padding for iOS
              paddingTop: "env(safe-area-inset-top)"
            },
            children: /* @__PURE__ */ jsxs(Container, { maxWidth, children: [
              /* @__PURE__ */ jsxs(
                Box,
                {
                  sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: 56,
                    py: 1
                  },
                  children: [
                    /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
                      showBack && /* @__PURE__ */ jsx(
                        IconButton,
                        {
                          onClick: handleBack,
                          edge: "start",
                          sx: {
                            color: isDarkMode ? "#FFFFFF" : "#000000"
                          },
                          children: /* @__PURE__ */ jsx(ArrowBackIcon, {})
                        }
                      ),
                      title && /* @__PURE__ */ jsxs(Box, { children: [
                        /* @__PURE__ */ jsx(
                          Typography,
                          {
                            variant: "h6",
                            component: "h1",
                            sx: {
                              fontWeight: 600,
                              color: isDarkMode ? "#FFFFFF" : "#000000"
                            },
                            children: title
                          }
                        ),
                        subtitle && /* @__PURE__ */ jsx(
                          Typography,
                          {
                            variant: "caption",
                            sx: {
                              color: isDarkMode ? alpha("#FFFFFF", 0.6) : alpha("#000000", 0.6)
                            },
                            children: subtitle
                          }
                        )
                      ] })
                    ] }),
                    rightAction && /* @__PURE__ */ jsx(Box, { children: rightAction })
                  ]
                }
              ),
              headerContent && /* @__PURE__ */ jsx(Box, { sx: { pb: 2 }, children: headerContent })
            ] })
          }
        ),
        /* @__PURE__ */ jsx(
          Container,
          {
            maxWidth,
            sx: {
              py: noPadding ? 0 : 2,
              px: noPadding ? 0 : void 0
            },
            children
          }
        )
      ]
    }
  );
}
function PageSection({ children, title, action, sx }) {
  const { isDarkMode } = useTheme();
  return /* @__PURE__ */ jsxs(Box, { sx: { mb: 3, ...sx }, children: [
    (title || action) && /* @__PURE__ */ jsxs(
      Box,
      {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2
        },
        children: [
          title && /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "subtitle1",
              sx: {
                fontWeight: 600,
                color: isDarkMode ? alpha("#FFFFFF", 0.87) : alpha("#000000", 0.87)
              },
              children: title
            }
          ),
          action && /* @__PURE__ */ jsx(Box, { children: action })
        ]
      }
    ),
    children
  ] });
}
function LoadingSpinner({ size = 40, text, fullScreen = false }) {
  const { currentTheme, isDarkMode } = useTheme();
  const content = /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2
      },
      children: [
        /* @__PURE__ */ jsx(
          CircularProgress,
          {
            size,
            sx: {
              color: currentTheme.colors.primary
            }
          }
        ),
        text && /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "body2",
            sx: {
              color: isDarkMode ? alpha("#FFFFFF", 0.7) : alpha("#000000", 0.7)
            },
            children: text
          }
        )
      ]
    }
  );
  if (fullScreen) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDarkMode ? alpha("#121212", 0.9) : alpha("#FFFFFF", 0.9),
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          zIndex: 9999
        },
        children: content
      }
    );
  }
  return content;
}
function LoadingOverlay({ loading, children, text }) {
  const { isDarkMode } = useTheme();
  return /* @__PURE__ */ jsxs(Box, { sx: { position: "relative" }, children: [
    children,
    loading && /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDarkMode ? alpha("#121212", 0.8) : alpha("#FFFFFF", 0.8),
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          borderRadius: "inherit",
          zIndex: 10
        },
        children: /* @__PURE__ */ jsx(LoadingSpinner, { text })
      }
    )
  ] });
}

export { BottomNav, GlassCard, GlassContainer, GlowEffect, LoadingOverlay, LoadingSpinner, PageLayout, PageSection };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map